import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { DeleteQuoteDialog } from '@/components/quotes/DeleteQuoteDialog'
import type { QuoteStatus } from '@limo/shared'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TablePagination } from '@/components/ui/table-pagination'
import { FileText, TrendingUp, Clock, CheckCircle } from 'lucide-react'
import {
  ColumnPicker,
  ExportButton,
  QuotesFilterBar,
  SavedViewsMenu,
  SortableHeader,
} from '@/components/reporting'
import { useUrlQuoteFilters } from '@/hooks/useUrlReportFilters'
import { useAccountTimezone, useQuoteReportStats, useQuotesReport } from '@/hooks/useReports'
import { useExportColumns } from '@/hooks/useExportColumns'
import {
  parseQuoteFilters,
  type QuoteFilters,
  type QuoteSortField,
  type SavedView,
} from '@limo/shared'

// Quote row as returned by the reporting query (booking_quotes + vehicles embed)
interface QuoteRow {
  id: string
  token: string
  status: QuoteStatus
  service_type: string
  pickup_address: string
  dropoff_address: string | null
  pickup_datetime: string
  customer_name: string | null
  customer_email: string | null
  customer_phone: string | null
  passenger_count: number | null
  vehicle_id: string | null
  vehicle_name: string | null
  total_amount: number | null
  created_at: string
  expires_at: string
  converted_at: string | null
  booking_id: string | null
  utm_source: string | null
  vehicles: { name: string } | null
}

// Status badge variants
const quoteStatusStyles: Record<
  QuoteStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  trip_details: { variant: 'secondary', label: 'Trip Details' },
  vehicle_selected: { variant: 'default', label: 'Vehicle Selected' },
  contact_provided: { variant: 'default', label: 'Contact Provided' },
  quote_requested: { variant: 'destructive', label: 'Quote Requested' },
  priced: { variant: 'default', label: 'Priced' },
  converted: { variant: 'outline', label: 'Converted' },
  expired: { variant: 'destructive', label: 'Expired' },
}

export function QuotesPage() {
  const navigate = useNavigate()
  // Deleting quotes (and their attached bookings) is platform-admin only —
  // the RLS policies enforce this server-side; the flag just hides the UI.
  const { isAdmin } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const timezone = useAccountTimezone()

  const columnCount = isAdmin ? 10 : 9

  // Build base path for navigation (supports impersonation)
  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  const {
    filters,
    sort,
    page,
    pageSize,
    setFilters,
    setSort,
    setPage,
    setPageSize,
    clearFilters,
    applyView,
  } = useUrlQuoteFilters()

  // Export column selection — CSV download only, not the table columns below.
  // Persisted per device so it survives a reload.
  const [selectedColumns, setSelectedColumns] = useExportColumns('quotes')

  const { data, isLoading, isError, error } = useQuotesReport(filters, sort, page, pageSize)
  // Counts come from the server and honour the active filters. Previously these
  // were derived from the fetched array, which max_rows capped at 1000 — so the
  // conversion rate was wrong for any account past that.
  const { data: stats } = useQuoteReportStats(filters)

  const quotes = (data?.rows ?? []) as unknown as QuoteRow[]
  const totalCount = data?.total ?? 0

  const handleApplyView = (view: SavedView): void => {
    applyView(
      parseQuoteFilters(view.filters as Record<string, unknown>),
      view.sort as { field: QuoteSortField; direction: 'asc' | 'desc' } | null
    )
    if (view.columns.length > 0) setSelectedColumns(view.columns)
  }

  // Format currency
  const formatCurrency = (amount: number | null) =>
    amount !== null
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(amount)
      : '—'

  // Format date
  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

  // Truncate address for display
  const truncateAddress = (address: string, maxLength = 30) => {
    if (address.length <= maxLength) return address
    return address.substring(0, maxLength) + '...'
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Quotes</h1>
          <p className="text-muted-foreground">
            View, filter and export customer quotes from the booking flow
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SavedViewsMenu
            resource="quotes"
            currentFilters={filters as Readonly<Record<string, unknown>>}
            currentSort={sort}
            currentColumns={selectedColumns}
            onApply={handleApplyView}
          />
          <ColumnPicker
            resource="quotes"
            selected={selectedColumns}
            onChange={setSelectedColumns}
          />
          <ExportButton
            resource="quotes"
            filters={filters}
            sort={sort}
            selectedColumns={selectedColumns}
            totalRows={totalCount}
          />
        </div>
      </div>

      {/* Stats Cards — these reflect the ACTIVE FILTERS, so they move with the
          table rather than describing a different data set. */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Quotes</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? stats.total.toLocaleString() : '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Quotes</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? stats.active.toLocaleString() : '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Converted</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? stats.converted.toLocaleString() : '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `${stats.conversionRate.toFixed(1)}%` : '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <QuotesFilterBar
        filters={filters}
        timezone={timezone}
        onChange={(next: QuoteFilters) => setFilters(next)}
        onClearAll={clearFilters}
      />

      {/* Quotes table */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle>All Quotes</CardTitle>
          <CardDescription>
            Customer quotes from the booking flow and their current status.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <p className="sr-only" aria-live="polite">
            {isLoading ? 'Loading quotes' : `${totalCount} quotes match the current filters`}
          </p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader field="status" label="Status" sort={sort} onSort={setSort} />
                  <TableHead>Source</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Trip</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Passengers</TableHead>
                  <SortableHeader
                    field="total_amount"
                    label="Total"
                    sort={sort}
                    onSort={setSort}
                  />
                  <SortableHeader field="created_at" label="Created" sort={sort} onSort={setSort} />
                  <SortableHeader field="expires_at" label="Expires" sort={sort} onSort={setSort} />
                  {isAdmin && <TableHead className="w-12" aria-label="Actions" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: columnCount }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : isError ? (
                  <TableRow>
                    <TableCell colSpan={columnCount} className="h-24 text-center text-destructive">
                      Could not load quotes: {error instanceof Error ? error.message : 'Unknown error'}
                    </TableCell>
                  </TableRow>
                ) : quotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columnCount} className="h-24 text-center">
                      No quotes match your filters. Clear them to see all quotes.
                    </TableCell>
                  </TableRow>
                ) : (
                  quotes.map((quote) => (
                    <TableRow
                      key={quote.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`${basePath}/quotes/${quote.id}`)}
                    >
                      <TableCell>
                        <Badge
                          variant={
                            quoteStatusStyles[quote.status as QuoteStatus]?.variant ||
                            'secondary'
                          }
                        >
                          {quoteStatusStyles[quote.status as QuoteStatus]?.label ||
                            quote.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {quote.utm_source ? (
                          <Badge variant="outline" className="text-xs">
                            {quote.utm_source}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">&mdash;</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {quote.customer_name || quote.customer_email ? (
                          <div>
                            <p className="font-medium">
                              {quote.customer_name || 'Unknown'}
                            </p>
                            {quote.customer_email && (
                              <p className="text-xs text-muted-foreground">
                                {quote.customer_email}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No contact</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[200px]">
                          <p className="font-medium text-sm truncate">
                            {truncateAddress(quote.pickup_address)}
                          </p>
                          {quote.dropoff_address && (
                            <p className="text-xs text-muted-foreground truncate">
                              to {truncateAddress(quote.dropoff_address)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      {/* Prefer the denormalised name so it survives vehicle deletion. */}
                      <TableCell>{quote.vehicle_name || quote.vehicles?.name || '—'}</TableCell>
                      <TableCell>{quote.passenger_count ?? '—'}</TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(quote.total_amount)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(quote.created_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {quote.status === 'converted' ? (
                          <span className="text-green-600">Converted</span>
                        ) : quote.status === 'expired' ? (
                          <span className="text-red-600">Expired</span>
                        ) : (
                          formatDate(quote.expires_at)
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell
                          className="w-12"
                          // The row itself navigates — keep clicks inside the
                          // actions cell (button + dialog) from doing both
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DeleteQuoteDialog
                            quoteId={quote.id}
                            customerName={quote.customer_name}
                            isConverted={quote.status === 'converted'}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {!isLoading && (
            <TablePagination
              page={page}
              pageSize={pageSize}
              count={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
